import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '../../../../lib/components';
import { TooltipComponent } from '../../../../lib/components/atomic/feedback';
import { TableDataGrid } from '../../../../lib/components/compound/tableDataGrid';
import { CheckmarkIcon, CloseIcon, SaveIcon } from '../../../../lib/icons';
import { useCreateImport } from '../hooks/useCreateImport';
import { useStepper } from '../../partials/actionStepperPartial';
import { PriorityHighRounded as WarningIcon } from '@mui/icons-material';
import Util from '../../../../misc/util';
import { useSnackbar } from 'notistack';

export const MapFieldsStep: FC = observer(() => {
  const { t } = useTranslation();

  const { setNextDisabled, setNextButtonConfig, activeStep, setActiveStep } = useStepper();
  const { newImport, createImport } = useCreateImport();
  const snackbar = useSnackbar();

  useEffect(() => {
    setNextButtonConfig({
      text: t('tasks:ACTION_BUTTON_import'),
      onClick: () => {
        createImport().then(() => {
          setActiveStep(activeStep + 1);
        });
      },
      endIcon: undefined,
      startIcon: <SaveIcon />
    });
  }, []);

  useEffect(() => {
    if (newImport.error) {
      snackbar.enqueueSnackbar({ message: newImport.error, variant: 'error' });
    }
    setNextDisabled(!!newImport.error);
  }, [newImport.error]);

  const renderPropCell = (params: GridRenderCellParams) => {
    return (
      <SearchableSelect
        value={(newImport.mappingRows && newImport.mappingRows[params.id as number].property) || ''}
        onChange={(option) => {
          newImport.updateRow(option as string, params.id as number);
        }}
        options={newImport.options}
        placeholder={t('form:MAP_FIELD')}
      />
    );
  };

  return (
    <>
      <TableDataGrid
        columns={[
          {
            field: 'column',
            headerName: t('common:COLUMN'),
            flex: 3
          },
          {
            field: 'preview',
            headerName: t('common:PREVIEW'),
            flex: 3,
            valueGetter: (params) => {
              const isExcelDate =
                /date|period|Datum|Geburtstag|Geb|Geburtsdatum|Eintritt|Eintrittsdatum|Austritt|Austrittsdatum/i.test(
                  params.row.column
                ) && typeof params.value === 'number';

              return isExcelDate ? Util.formatExcelNumberToDate(params.value) : params.value;
            }
          },
          {
            field: 'property',
            headerName: t('common:PROPERTY'),
            flex: 3,
            renderCell: renderPropCell
          },
          {
            field: 'isMapped',
            headerName: t('common:MAPPED'),
            flex: 1,
            renderCell: renderMappedCell
          }
        ]}
        rows={newImport.mappingRows || []}
        checkboxSelection={false}
        hideFooter
        components={{ Toolbar: undefined }}
      />
    </>
  );
});

export const renderMappedCell = (params: GridRenderCellParams) => {
  const icons = {
    mapped: CheckmarkIcon,
    unmapped: CloseIcon,
    warning: WarningIcon
  };

  const ICON_STYLES = {
    color: 'common.white',
    backgroundColor: params.row.property
      ? params.row.hasError
        ? 'warning.main'
        : 'success.main'
      : 'grey.100',
    borderRadius: '50%'
  };

  return params.row.property ? (
    params.row.hasError ? (
      <TooltipComponent title={params.row.errorMessage}>
        <icons.warning sx={ICON_STYLES} />
      </TooltipComponent>
    ) : (
      <icons.mapped sx={ICON_STYLES} />
    )
  ) : (
    <icons.unmapped sx={ICON_STYLES} />
  );
};
